/*
 * Copyright 2024, Corning Optical Communications
 * All Rights Reserved.
 */

/**
 * MA3000 Platform Configuration
 * Defines MA3000-specific behavior for topology display and interaction
 */

import { IInventoryGraphNode, IRemoteEquipment } from "../../../store/api/devices/types";
import { PlatformConfig } from "./PlatformConfig";
import { ma3000HierarchyBuilder } from "../builders/MA3000HierarchyBuilder";
import { getAlarmLedColor, parseDeviceAttributes } from "../../Drawers/DasDeviceDetails/utils";

export class MA3000PlatformConfig implements PlatformConfig {
  platformId = "ma3000";
  displayName = "MA3000";
  hierarchyBuilder = ma3000HierarchyBuilder;

  matches(platformId: string): boolean {
    const normalized = platformId?.toLowerCase().replace(/[-_\s]/g, "") || "";
    return normalized.includes("ma3000") || normalized === "ma3000";
  }

  findDeviceForNode(
    nodeId: string,
    node: IInventoryGraphNode | undefined,
    listData: IRemoteEquipment[],
    rootId: string
  ): IRemoteEquipment | null {
    // Special handling for MA-3000 root node - open HCM drawer
    if (node && (nodeId === rootId || node.equipmentType === "MA-3000" || node.equipmentType === "-3000")) {
      const hcmDevice = listData.find((d) => d.equipmentType === "HCM");
      if (hcmDevice) {
        console.log("[MA3000] Opening HCM device for root node");
        return hcmDevice;
      }
    }

    // Priority 1: Match by objectKey (most reliable)
    let device = listData.find((d) => d.objectKey === nodeId);
    if (device) {
      console.log("[MA3000] Found device by objectKey:", device.objectKey, device.equipmentType);
      return device;
    }

    // Priority 2: Match by managedObject
    device = listData.find((d) => d.managedObject === nodeId);
    if (device) {
      console.log("[MA3000] Found device by managedObject:", device.managedObject, device.equipmentType);
      return device;
    }

    // Priority 3: Match by IP address (more specific than hostname)
    if (node?.ipAddress) {
      device = listData.find((d) => d.ipAddress === node.ipAddress);
      if (device) {
        console.log("[MA3000] Found device by ipAddress:", device.ipAddress, device.equipmentType);
        return device;
      }
    }

    // Priority 4: Match by hostname/label (least reliable)
    if (node?.label) {
      device = listData.find((d) => d.hostname === node.label);
      if (device) {
        console.log("[MA3000] Found device by hostname/label (FALLBACK):", device.hostname, device.equipmentType);
        return device;
      }
    }

    console.warn("[MA3000] No device found for node:", nodeId, node);
    return null;
  }

  getRootLedColor(listData: IRemoteEquipment[]): string {
    const hcmDevice = listData.find((d) => d.equipmentType === "HCM");
    if (!hcmDevice) return "#9e9e9e"; // Gray if no HCM found

    const isDisconnected = hcmDevice.status?.toString() === "1" || hcmDevice.status?.toString() === "19";

    // Check for active alarms from attributes
    const parsedAttributes = parseDeviceAttributes(hcmDevice.attributes);
    const alarms = parsedAttributes?.alarms || 0;
    const alarmsMask = parsedAttributes?.alarmsMask || 0;
    const hasActiveAlarms = (alarms & alarmsMask) !== 0;

    return getAlarmLedColor(isDisconnected, hasActiveAlarms);
  }

  metadata = {
    rootEquipmentType: "HCM",
    chassisTypes: ["HEU", "OIU", "IHU"],
  };
}
